import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  Package,
  Star
} from 'lucide-react';

const MOCK_TENANT_ID = localStorage.getItem('tenant_id') || 'tenant-123';

/**
 * SupplierSkuManager - Manages supplier SKU mappings for a product
 * 
 * Purpose: Allow users to map their internal Master SKU to multiple supplier SKUs
 * Example: Master SKU 4723 maps to Supplier SKU 7124 from "Advance Pet Products"
 * 
 * When creating a PO, searching "7124" finds Master SKU 4723
 * Preferred supplier's cost is auto-filled when adding to PO
 */
const SupplierSkuManager = ({ productId, productSku, onMappingChange }) => {
  // State
  const [supplierSkus, setSupplierSkus] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form state for adding/editing
  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    supplier_sku: '',
    unit_cost: 0,
    moq: 1,
    lead_time_days: 7,
    is_preferred: false
  });

  // Fetch supplier SKU mappings
  const fetchSupplierSkus = async () => {
    if (!productId) return;
    
    setLoading(true);
    try {
      const response = await api.get(
        `/api/v1/product-master/item/${productId}/supplier-skus?tenant_id=${MOCK_TENANT_ID}`,
        { headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
      );
      
      setSupplierSkus(response.supplier_skus || []);
    } catch (error) {
      console.error('Error fetching supplier SKUs:', error);
      // Don't show error toast for 404 (product may not have mappings yet)
      if (error.response?.status !== 404) {
        toast.error('Failed to load supplier SKU mappings');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch suppliers list for dropdown
  const fetchSuppliers = async () => {
    try {
      const response = await api.get(`/api/v1/suppliers`, {
        headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
      });
      
      if (Array.isArray(response)) {
        setSuppliers(response);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  // Load data on mount
  useEffect(() => {
    if (productId) {
      fetchSupplierSkus();
    }
    fetchSuppliers();
  }, [productId]); // productId is the main dependency

  // Reset form
  const resetForm = () => {
    setFormData({
      supplier_id: '',
      supplier_name: '',
      supplier_sku: '',
      unit_cost: 0,
      moq: 1,
      lead_time_days: 7,
      is_preferred: false
    });
    setShowAddForm(false);
    setEditingId(null);
  };

  // Handle supplier selection
  const handleSupplierSelect = (e) => {
    const supplierId = e.target.value;
    const supplier = suppliers.find(s => s.id === supplierId);
    
    setFormData(prev => ({
      ...prev,
      supplier_id: supplierId,
      supplier_name: supplier?.name || ''
    }));
  };

  // Set preferred supplier
  const handleSetPreferred = async (supplierId) => {
    setLoading(true);
    try {
      await api.post(
        `/api/v1/product-master/item/${productId}/supplier-skus/${supplierId}/set-preferred?tenant_id=${MOCK_TENANT_ID}`,
        {},
        { headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
      );
      
      toast.success('Preferred supplier updated');
      fetchSupplierSkus();
      onMappingChange?.();
    } catch (error) {
      console.error('Error setting preferred supplier:', error);
      toast.error('Failed to set preferred supplier');
    } finally {
      setLoading(false);
    }
  };

  // Add new mapping
  const handleAddMapping = async () => {
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    if (!formData.supplier_sku.trim()) {
      toast.error('Please enter the supplier SKU');
      return;
    }

    setLoading(true);
    try {
      await api.post(
        `/api/v1/product-master/item/${productId}/supplier-skus?tenant_id=${MOCK_TENANT_ID}`,
        formData,
        { headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
      );
      
      toast.success('Supplier SKU mapping added', {
        description: `${formData.supplier_name}: ${formData.supplier_sku}`
      });
      
      resetForm();
      fetchSupplierSkus();
      onMappingChange?.();
    } catch (error) {
      console.error('Error adding supplier SKU:', error);
      const message = error.response?.data?.detail || 'Failed to add mapping';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Update existing mapping
  const handleUpdateMapping = async () => {
    if (!formData.supplier_sku.trim()) {
      toast.error('Please enter the supplier SKU');
      return;
    }

    setLoading(true);
    try {
      await api.put(
        `/api/v1/product-master/item/${productId}/supplier-skus/${editingId}?tenant_id=${MOCK_TENANT_ID}`,
        formData,
        { headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
      );
      
      toast.success('Supplier SKU mapping updated');
      resetForm();
      fetchSupplierSkus();
      onMappingChange?.();
    } catch (error) {
      console.error('Error updating supplier SKU:', error);
      const message = error.response?.data?.detail || 'Failed to update mapping';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Delete mapping
  const handleDeleteMapping = async (supplierId, supplierName) => {
    if (!window.confirm(`Remove SKU mapping from "${supplierName}"?`)) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(
        `/api/v1/product-master/item/${productId}/supplier-skus/${supplierId}?tenant_id=${MOCK_TENANT_ID}`,
        { headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
      );
      
      toast.success('Supplier SKU mapping removed');
      fetchSupplierSkus();
      onMappingChange?.();
    } catch (error) {
      console.error('Error deleting supplier SKU:', error);
      const message = error.response?.data?.detail || 'Failed to delete mapping';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Start editing a mapping
  const handleStartEdit = (mapping) => {
    setFormData({
      supplier_id: mapping.supplier_id,
      supplier_name: mapping.supplier_name,
      supplier_sku: mapping.supplier_sku,
      unit_cost: mapping.unit_cost || 0,
      moq: mapping.moq || 1,
      lead_time_days: mapping.lead_time_days || 7,
      is_preferred: mapping.is_preferred || false
    });
    setEditingId(mapping.supplier_id);
    setShowAddForm(false);
  };

  // Check if supplier already has a mapping
  const supplierHasMapping = (supplierId) => {
    return supplierSkus.some(s => s.supplier_id === supplierId);
  };

  // Get available suppliers (not already mapped)
  const availableSuppliers = suppliers.filter(s => !supplierHasMapping(s.id));

  return (
    <div className="space-y-4" data-testid="supplier-sku-manager">
      {/* Section Header */}
      <div className="flex justify-between items-center border-b border-gray-700 pb-2">
        <h4 className="text-gray-300 text-xs font-medium uppercase tracking-wide flex items-center gap-2">
          <Package className="w-4 h-4 text-orange-400" />
          Supplier SKU Mappings
        </h4>
        {!showAddForm && !editingId && (
          <Button
            onClick={() => setShowAddForm(true)}
            size="sm"
            className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700"
            disabled={loading || availableSuppliers.length === 0}
            data-testid="add-supplier-sku-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Mapping
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingId) && (
        <div className="bg-[#0d1b2a] border border-gray-700 rounded-lg p-3 space-y-3">
          <div className="text-xs font-medium text-white mb-2">
            {editingId ? 'Edit Mapping' : 'Add New Supplier SKU Mapping'}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {/* Supplier Dropdown */}
            <div>
              <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-1 block">
                Supplier *
              </label>
              <select
                value={formData.supplier_id}
                onChange={handleSupplierSelect}
                disabled={!!editingId}
                className="w-full text-xs h-8 px-2 bg-[#0f1729] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500 disabled:opacity-50"
                data-testid="supplier-select"
              >
                <option value="">Select supplier...</option>
                {(editingId ? suppliers : availableSuppliers).map(supplier => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Supplier SKU */}
            <div>
              <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-1 block">
                Supplier SKU *
              </label>
              <Input
                type="text"
                value={formData.supplier_sku}
                onChange={(e) => setFormData(prev => ({ ...prev, supplier_sku: e.target.value }))}
                placeholder="e.g., 7124"
                className="h-8 text-xs bg-[#0f1729] border-gray-700 text-white"
                data-testid="supplier-sku-input"
              />
            </div>

            {/* Unit Cost */}
            <div>
              <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-1 block">
                Unit Cost ($)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.unit_cost}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_cost: parseFloat(e.target.value) || 0 }))}
                placeholder="0.00"
                className="h-8 text-xs bg-[#0f1729] border-gray-700 text-white"
                data-testid="unit-cost-input"
              />
            </div>

            {/* MOQ */}
            <div>
              <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-1 block">
                MOQ
              </label>
              <Input
                type="number"
                min="1"
                value={formData.moq}
                onChange={(e) => setFormData(prev => ({ ...prev, moq: parseInt(e.target.value) || 1 }))}
                placeholder="1"
                className="h-8 text-xs bg-[#0f1729] border-gray-700 text-white"
                data-testid="moq-input"
              />
            </div>

            {/* Lead Time */}
            <div>
              <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-1 block">
                Lead Time (days)
              </label>
              <Input
                type="number"
                min="0"
                value={formData.lead_time_days}
                onChange={(e) => setFormData(prev => ({ ...prev, lead_time_days: parseInt(e.target.value) || 0 }))}
                placeholder="7"
                className="h-8 text-xs bg-[#0f1729] border-gray-700 text-white"
                data-testid="lead-time-input"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-700">
            <Button
              variant="outline"
              size="sm"
              onClick={resetForm}
              className="h-7 px-3 text-xs border-gray-600 text-gray-300 hover:bg-gray-700"
              data-testid="cancel-mapping-btn"
            >
              <X className="w-3 h-3 mr-1" />
              Cancel
            </Button>
            <Button
              onClick={editingId ? handleUpdateMapping : handleAddMapping}
              size="sm"
              disabled={loading || !formData.supplier_id || !formData.supplier_sku.trim()}
              className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-mapping-btn"
            >
              <Save className="w-3 h-3 mr-1" />
              {editingId ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      {/* Mappings Table */}
      {supplierSkus.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="supplier-sku-table">
            <thead className="bg-[#0d1b2a]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                  Supplier
                </th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                  Their SKU
                </th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                  Cost
                </th>
                <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                  MOQ
                </th>
                <th className="px-3 py-2 w-12 text-center text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                  Pref
                </th>
                <th className="px-3 py-2 w-20 text-center text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {supplierSkus.map((mapping) => (
                <tr 
                  key={mapping.supplier_id} 
                  className="hover:bg-[#0f1729]/50"
                  data-testid={`supplier-row-${mapping.supplier_id}`}
                >
                  <td className="px-3 py-2 text-white">
                    {mapping.supplier_name}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-orange-400 font-medium">
                      {mapping.supplier_sku}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-400">
                    ${(mapping.unit_cost || 0).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-300">
                    {mapping.moq || 1}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => handleSetPreferred(mapping.supplier_id)}
                      className={`p-1 rounded transition-colors ${
                        mapping.is_preferred 
                          ? 'text-yellow-400 hover:text-yellow-300' 
                          : 'text-gray-600 hover:text-gray-400 hover:bg-gray-700/50'
                      }`}
                      title={mapping.is_preferred ? 'Preferred supplier' : 'Set as preferred'}
                      disabled={loading}
                      data-testid={`preferred-${mapping.supplier_id}`}
                    >
                      <Star 
                        className={`w-4 h-4 ${mapping.is_preferred ? 'fill-yellow-400' : ''}`} 
                      />
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center gap-1">
                      <button
                        onClick={() => handleStartEdit(mapping)}
                        className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded"
                        title="Edit mapping"
                        disabled={loading || editingId || showAddForm}
                        data-testid={`edit-mapping-${mapping.supplier_id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteMapping(mapping.supplier_id, mapping.supplier_name)}
                        className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                        title="Delete mapping"
                        disabled={loading}
                        data-testid={`delete-mapping-${mapping.supplier_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-[#0d1b2a] border border-gray-700 rounded-lg p-4 text-center">
          <Package className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 text-xs">
            No supplier SKU mappings yet.
          </p>
          <p className="text-gray-500 text-[10px] mt-1">
            Add mappings to search by supplier SKU when creating Purchase Orders.
          </p>
        </div>
      )}

      {/* Info Note */}
      <div className="text-[10px] text-gray-500 bg-[#0d1b2a] p-2 rounded border border-gray-800">
        <strong className="text-gray-400">Tip:</strong> Click the ⭐ star to mark a supplier as preferred. 
        The preferred supplier&apos;s cost will auto-fill when adding this product to a PO.
      </div>
    </div>
  );
};

export default SupplierSkuManager;
